import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, ExternalLink, FileText, X } from 'lucide-react';
import './ResumeModal.css';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Google Drive takes a couple of seconds to load its viewer and draw the PDF, so it is not loaded
// for every visitor. It starts when someone shows intent (`preload`: hovering, focusing or touching
// the "View resume" button), which gives it a head start, or right away if the dialog is opened.
// Once built, the dialog stays mounted (hidden), so reopening it is instant.
// The iframe's load event fires before Drive has drawn the PDF, so the loader stays a little longer.
// The failsafe lifts it anyway if the load event never arrives (blocked or offline), leaving the
// "open in a new tab" button as the way out.
const SETTLE_MS = 700;
const FAILSAFE_MS = 8000;

const ResumeModal = ({ open, preload, onClose, previewUrl, downloadUrl }) => {
  const panelRef = useRef(null);
  const closeRef = useRef(null);
  const [warm, setWarm] = useState(false);
  const [ready, setReady] = useState(false);
  const settleTimer = useRef(null);

  // The Drive "/preview" link is the embeddable version; "/view" is the full page.
  const openUrl = previewUrl.replace(/\/preview$/, '/view');

  // Keep the latest onClose in a ref, so the open/close effect below only reacts to `open`
  // (the parent passes a new function on every render, which would otherwise re-run it).
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Start loading on intent (preload) or when opened; never before.
  useEffect(() => {
    if (!warm && (open || preload)) setWarm(true);
  }, [open, preload, warm]);

  // Failsafe so the loader can never spin forever.
  useEffect(() => {
    if (!warm) return undefined;
    const failsafe = setTimeout(() => setReady(true), FAILSAFE_MS);
    return () => {
      clearTimeout(failsafe);
      clearTimeout(settleTimer.current);
    };
  }, [warm]);

  const onFrameLoad = useCallback(() => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => setReady(true), SETTLE_MS);
  }, []);

  // Keyboard and focus behaviour of a proper dialog while it is open: lock page scroll, move focus
  // in, trap Tab inside, close on Escape, and hand focus back to the button that opened it.
  useEffect(() => {
    if (!open) return undefined;

    const opener = document.activeElement;
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const items = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      root.style.overflow = previousOverflow;
      if (opener && opener.focus) opener.focus();
    };
  }, [open]);

  if (!warm) return null;

  return createPortal(
    <div
      className={`rm-overlay ${open ? 'is-open' : ''}`}
      aria-hidden={!open}
      onMouseDown={(e) => {
        // a click on the dimmed backdrop (not on the panel) closes the dialog
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} className="rm-panel" role="dialog" aria-modal="true" aria-labelledby="rm-title">
        <header className="rm-head">
          <span className="rm-icon" aria-hidden="true">
            <FileText size={20} />
          </span>
          <div className="rm-titles">
            <h2 id="rm-title">Resume</h2>
            <p>Venkata Phani Charan Udumula</p>
          </div>

          <div className="rm-actions">
            <a
              href={downloadUrl}
              className="rm-btn"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Download resume"
            >
              <Download size={16} />
              <span>Download</span>
            </a>
            <a
              href={openUrl}
              className="rm-icon-btn"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open resume in a new tab"
              title="Open in a new tab"
            >
              <ExternalLink size={18} />
            </a>
            <button type="button" ref={closeRef} className="rm-icon-btn" onClick={onClose} aria-label="Close resume">
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="rm-body">
          <div className="rm-frame">
            <iframe src={previewUrl} title="Resume preview" allow="autoplay" onLoad={onFrameLoad} />
            {/* Only visible if the dialog is opened before the PDF has finished drawing */}
            <div className={`rm-loading ${ready ? 'is-done' : ''}`} role="status" aria-hidden={ready}>
              <span className="rm-spinner" aria-hidden="true" />
              Loading resume...
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ResumeModal;
